import { logger } from '@/shared/helpers/logger';
import { notify } from '@/viewmodel/shared/notify';
import { useCallback, useRef } from 'react';

type AsyncMutationOptions<TArgs extends unknown[]> = {
  logSource: string;
  logContext: string;
  errorContext?: Record<string, unknown> | ((...args: TArgs) => Record<string, unknown>);
  errorToast?: string;
  successToast?: string;
  swallow?: boolean;
};

export function useAsyncMutation<TArgs extends unknown[], TReturn>(
  fn: (...args: TArgs) => Promise<TReturn>,
  options: AsyncMutationOptions<TArgs>,
): (...args: TArgs) => Promise<TReturn> {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const optsRef = useRef(options);
  optsRef.current = options;

  return useCallback(async (...args: TArgs): Promise<TReturn> => {
    const {
      logSource,
      logContext,
      errorContext,
      errorToast,
      successToast,
      swallow = false,
    } = optsRef.current;
    try {
      const result = await fnRef.current(...args);
      if (successToast) notify(successToast, 'success');
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      const ctx = typeof errorContext === 'function' ? errorContext(...args) : errorContext;
      logger.error(logContext, { ...ctx, log_source: logSource, error });
      if (errorToast) notify(errorToast, 'danger');
      if (!swallow) throw err;
      return undefined as TReturn;
    }
  }, []);
}
